import { useQuery } from '@tanstack/react-query';
import { Col, Row, Spin, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useCurrency } from '@/contexts/CurrencyContext';
import { AIWelcomeCard, AIExecutiveSummaryCard, AIPromptCard } from '@/components/ai-design-system';
import { fetchWorkspaceHome, fetchPins, type ConversationMode } from '@/services/aiAgent.service';

const { Title } = Typography;

const PROMPTS_BY_MODE: Record<ConversationMode, Array<{ title: string; subtitle?: string; prompt: string }>> = {
  executive: [
    { title: 'What should I worry about today?', prompt: 'What should I worry about today?' },
    { title: 'Analyze cash flow', prompt: 'Where is money stuck?' },
    { title: 'Show receivables risk', prompt: 'Show AR aging buckets.' },
    { title: 'Production health', prompt: 'Which work orders are delayed?' },
    { title: 'Delivery risks', prompt: 'Show deliveries delayed.' },
    { title: 'Executive risk snapshot', prompt: 'What predictive risks should I watch?' },
  ],
  analyst: [
    { title: 'Sales trend analysis', prompt: 'Show sales trend this quarter.' },
    { title: 'Variance analysis', prompt: 'Compare sales vs collections this month.' },
    { title: 'Root cause — production', prompt: 'Why is production delayed?' },
    { title: 'Supplier performance', prompt: 'Show supplier delay leaderboard.' },
    { title: 'Inventory aging', prompt: 'Show slow moving inventory.' },
    { title: 'Cross-module trace', prompt: 'Trace receivables by customer and project.' },
  ],
  assistant: [
    { title: 'Pending approvals', prompt: 'What are my pending approvals?' },
    { title: 'My tasks today', prompt: 'What are my pending tasks today?' },
    { title: 'Pending dispatch', prompt: 'Show pending dispatch deliveries.' },
    { title: 'Open purchase orders', prompt: 'What is open PO value?' },
    { title: 'Low stock items', prompt: 'Which items are below reorder level?' },
    { title: 'Overdue invoices', prompt: 'Show overdue customer invoices.' },
  ],
};

export interface AIWorkspaceHomeProps {
  mode: ConversationMode;
  onPrompt: (text: string) => void;
}

export function AIWorkspaceHome({ mode, onPrompt }: AIWorkspaceHomeProps) {
  const user = useAuthStore((s) => s.user);
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const name = user?.first_name || user?.username || 'there';

  const homeQ = useQuery({
    queryKey: ['workspace-home'],
    queryFn: fetchWorkspaceHome,
    staleTime: 60_000,
  });

  const pinsQ = useQuery({
    queryKey: ['workspace-pins-preview'],
    queryFn: () => fetchPins(),
    staleTime: 60_000,
  });

  const prompts = PROMPTS_BY_MODE[mode] || PROMPTS_BY_MODE.executive;

  return (
    <div className="ai-workspace-chat-inner" style={{ paddingBottom: 32 }}>
      <AIWelcomeCard userName={name} />
      <Spin spinning={homeQ.isLoading}>
        <AIExecutiveSummaryCard
          userName={name}
          kpis={homeQ.data?.kpis || []}
          formatCurrency={formatCurrency}
          loading={homeQ.isLoading}
        />
      </Spin>
      <Title level={5} style={{ marginBottom: 12 }}>
        Suggested questions
      </Title>
      <Row gutter={[12, 12]} style={{ marginBottom: 28 }}>
        {prompts.map((p) => (
          <Col xs={24} sm={12} md={8} key={p.prompt}>
            <AIPromptCard title={p.title} subtitle={p.subtitle} onClick={() => onPrompt(p.prompt)} />
          </Col>
        ))}
      </Row>
      {(pinsQ.data?.length || 0) > 0 ? (
        <>
          <Title level={5} style={{ marginBottom: 8 }}>
            Pinned analytics
          </Title>
          <Row gutter={[8, 8]}>
            {pinsQ.data!.slice(0, 4).map((pin) => (
              <Col span={12} key={pin.id}>
                <div
                  className="ai-prompt-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate('/ai/dashboard')}
                  onKeyDown={(e) => e.key === 'Enter' && navigate('/ai/dashboard')}
                >
                  {pin.title}
                </div>
              </Col>
            ))}
          </Row>
        </>
      ) : null}
    </div>
  );
}
